export function money(value: number): string {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    maximumFractionDigits: 0,
  }).format(Math.max(0, Math.round(value)))
}

/**
 * Same as `money`, but keeps cents when the value isn't a whole dollar
 * (e.g. an effective per-license or per-enrolled-student rate). Whole-dollar
 * values still render without decimals, so this never adds a stray ".00".
 * Added for the pricing-v2 "one number": $29,700 over 1,000 licenses must
 * show $29.70, not round to $30.
 */
export function preciseMoney(value: number): string {
  const cents = Math.round(Math.max(0, value) * 100)
  const normalized = cents / 100
  const isWhole = Number.isInteger(normalized)
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: isWhole ? 0 : 2,
    maximumFractionDigits: 2,
  }).format(normalized)
}

// Unit prices keep their cents. A per-license price of $2.60 or $3.50 must
// never print as a whole dollar, or a wrong stored price hides behind a
// rounded label while the line total stays wrong.
export function unitMoney(value: number): string {
  const whole = Number.isInteger(Math.round(value * 100) / 100) && Math.abs(value - Math.round(value)) < 0.005
  return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', minimumFractionDigits: whole ? 0 : 2, maximumFractionDigits: 2 }).format(Math.max(0, value))
}

/** Like `money`, but a negative amount (a credit) keeps its minus sign: "-$22,039". */
export function signedMoney(value: number): string {
  return value < 0 && Math.round(value) !== 0 ? `-${money(-value)}` : money(value)
}

/** Like `unitMoney`, but a negative unit price (a credit) keeps its minus sign. */
export function signedUnitMoney(value: number): string {
  return value < 0 ? `-${unitMoney(-value)}` : unitMoney(value)
}

export function compactMoney(value: number): string {
  const absolute = Math.max(0, value)
  if (absolute >= 1_000_000) return `$${(absolute / 1_000_000).toFixed(1).replace('.0', '')}M`
  if (absolute >= 10_000) return `$${Math.round(absolute / 1_000)}K`
  return money(absolute)
}

export function number(value: number): string {
  return new Intl.NumberFormat('en-US', { maximumFractionDigits: 0 }).format(Math.max(0, Math.round(value)))
}

export function shortDate(value: string | null): string {
  if (!value) return 'Date to confirm'
  return new Date(`${value}T12:00:00`).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })
}

export function initials(name: string): string {
  return name.split(/[\s,.]+/).filter(Boolean).slice(0, 2).map((part) => part[0]?.toUpperCase()).join('')
}

/** "Nov 4, 2026, 10:02 AM": one format for history rows, threads and the admin log. */
export function dateTime(value: string): string {
  return new Date(value).toLocaleString('en-US', { month: 'short', day: 'numeric', year: 'numeric', hour: 'numeric', minute: '2-digit' })
}
